import {
  useCreatePromotion,
  usePromotion,
  usePromotionCoupons,
  useUpdatePromotion,
} from '@/features/promotions/hooks/usePromotions';
import { useMenuTrees, useMenus } from '@/features/menu/hooks/useMenu';
import { CouponHistoryList } from '@/features/promotions/components/CouponHistoryList';
import { IssueCouponSection } from '@/features/promotions/components/IssueCouponSection';
import {
  WeeklyScheduleSection,
  type ScheduleWindow,
} from '@/features/promotions/components/WeeklyScheduleSection';
import {
  buildPromotionBody,
  emptyDraft,
  hydrateDraft,
  type PromotionBuildError,
  type PromotionDraft,
} from '@/features/promotions/domain';
import {
  categoryState,
  checkedProductCount,
  isProductChecked,
  toggleCategory,
  toggleProduct,
  type TargetCategory,
} from '@/features/promotions/utils/promotion-targets';
import { ChipInput } from '@/components/ChipInput';
import { TriStateCheckbox } from '@/components/TriStateCheckbox';
import { featureFlags } from '@/featureFlags';
import MonthCalendarPopover from '@/components/MonthCalendarPopover';
import SettingsSaveNav from '@/components/SettingsSaveNav';
import { useT } from '@/i18n';
import { useRestaurantStore, useRestaurantTimezone } from '@/stores/restaurant.store';
import { loc } from '@/utils/localized';
import { setUnsavedDirty } from '@/utils/unsavedChanges';
import type { CreatePromotionBody, PromotionPaymentMethodKind } from '@restaurant/api-client';
import type { Router } from 'framework7/types';
import {
  AccordionContent,
  Block,
  BlockTitle,
  Button,
  List,
  ListInput,
  ListItem,
  Navbar,
  Page,
  Segmented,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { type MouseEvent, useEffect, useRef, useState } from 'react';

type EditMode = 'simple' | 'advanced';

// A promotion needs Advanced mode when it uses any knob Simple mode hides. Used
// to auto-open the right mode when editing an existing promotion (and to warn
// when advanced values are set but hidden). Simple mode = automatic, whole/
// category/product discount with no code, schedule, min, stacking or caps.
function usesAdvancedFeatures(p: {
  trigger: Trigger;
  weeklySchedule: ScheduleWindow[] | null;
  startsAt: string | null;
  endsAt: string | null;
  minSubtotalCents: number;
  stackable: boolean;
  priority: number;
  maxDiscountCents: number | null;
  customerVisible: boolean;
  orderTypes: OrderType[];
  paymentMethodKinds: PromotionPaymentMethodKind[];
}): boolean {
  return (
    p.trigger !== 'AUTOMATIC' ||
    (p.weeklySchedule?.length ?? 0) > 0 ||
    p.minSubtotalCents > 0 ||
    p.stackable ||
    p.priority !== 0 ||
    p.maxDiscountCents != null ||
    !p.customerVisible ||
    // Anything other than the default "both online channels" is advanced.
    p.orderTypes.filter((ot) => ALL_ORDER_TYPES.includes(ot)).length !== ALL_ORDER_TYPES.length ||
    p.paymentMethodKinds.length > 0
  );
}

type Trigger = 'AUTOMATIC' | 'CODE_REQUIRED';
type OrderType = 'DINE_IN' | 'PICKUP' | 'DELIVERY';

// Online ordering is pickup + delivery only - dine-in lives in table-ordering.
const ALL_ORDER_TYPES: OrderType[] = ['PICKUP', 'DELIVERY'];

const ALL_PAYMENT_METHOD_KINDS: PromotionPaymentMethodKind[] = [
  'CASH',
  'CARD_ON_HANDOFF',
  'ONLINE',
  'MANUAL',
];

/**
 * Tap-to-pick row: a `<ListItem>` showing the current value that opens a
 * one-group `f7.actions` menu (label + options + Cancel in a SINGLE group, per
 * FRONTEND_PRACTICES). `installActionAnchor` anchors it as a popover on desktop
 * and a bottom sheet on phones - the app-wide idiom, replacing a native
 * `<select>` so dropdowns look and behave the same everywhere.
 *
 * INVOKE AS A FUNCTION - `{SelectRow({...})}`, never `<SelectRow/>`. F7's
 * `<List>` only wraps a child in its `<ul>` (and applies the list-reset that
 * kills the browser's default `<li>` disc bullet + gives the inset-card look)
 * when the child's element type is a literal `ListItem`. A wrapper COMPONENT
 * renders outside the `<ul>`, leaking the bullet and dropping the card styling.
 * Returning the `ListItem` from a plain function keeps the type intact - same
 * pattern as `OrderCard` / `ReservationCard` (see FRONTEND_PRACTICES Rule 9).
 */
function SelectRow<T extends string>({
  label,
  value,
  options,
  onChange,
  cancelText,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  cancelText: string;
}) {
  const current = options.find((o) => o.value === value)?.label ?? '';
  const open = (e: MouseEvent<HTMLElement>) => {
    // Anchor the popover to the row's left-hand title so it pops on the LEFT
    // (like the language picker), not centred/right under the press point.
    const li = e.currentTarget.closest('li');
    const targetEl = (li?.querySelector('.item-title') ?? e.currentTarget) as HTMLElement;
    f7.actions
      .create({
        targetEl,
        buttons: [
          [
            { label: true, text: label },
            ...options.map((o) => ({
              text: o.label,
              bold: o.value === value,
              onClick: () => onChange(o.value),
            })),
            { text: cancelText, color: 'red' },
          ],
        ],
      })
      .open();
  };
  return <ListItem link="#" title={label} after={current} onClick={open} />;
}

/** Compact label for a date pill - matches the reservation date chip format. */
function formatDateChip(value: string): string {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/** Date-pill trigger styling - mirrors the reservation create/edit date chip. */
const DATE_PILL_STYLE = {
  background: 'rgba(120,120,128,0.12)',
  borderRadius: 6,
  padding: '4px 10px',
  border: 'none',
  cursor: 'pointer',
  fontSize: 17,
  lineHeight: '24px',
  color: 'var(--f7-theme-color)',
  whiteSpace: 'nowrap',
} as const;

function PromotionEditPage({
  f7route,
  f7router,
}: {
  f7route: { params: { promotionId: string } };
  f7router: Router.Router;
}) {
  const routeId = f7route.params.promotionId;
  const isNew = routeId === 'new';
  const { data: existing } = usePromotion(isNew ? null : routeId);
  const create = useCreatePromotion();
  const update = useUpdatePromotion();
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const tz = useRestaurantTimezone();
  const t = useT();
  const tp = t.promotions;
  const orderTypeLabel: Record<OrderType, string> = {
    DINE_IN: tp.orderType.dineIn,
    PICKUP: tp.orderType.pickup,
    DELIVERY: tp.orderType.delivery,
  };
  const paymentMethodLabel: Record<PromotionPaymentMethodKind, string> = {
    CASH: tp.paymentMethod.cash,
    CARD_ON_HANDOFF: tp.paymentMethod.cardOnHandoff,
    ONLINE: tp.paymentMethod.online,
    MANUAL: tp.paymentMethod.manual,
  };

  // Source targets from EVERY menu, not just the active one: a restaurant can
  // run several menus (one per channel), so a category/product the owner wants
  // to discount may live in a menu that isn't the "active" one. Aggregating
  // avoids silently showing an empty picker.
  const { data: menus } = useMenus();
  const {
    trees: menuTrees,
    isLoading: itemsLoading,
    isError: itemsError,
  } = useMenuTrees((menus ?? []).map((m) => m.id));

  // Every payload-bound form value lives in one typed draft (create-form
  // defaults from emptyDraft; start/end windows are separate date "YYYY-MM-DD" +
  // time "HH:mm", combined into a UTC ISO on save). Reads are `draft.X`; writes
  // go through `set('X', value)` (or setDraft for functional updates).
  const [draft, setDraft] = useState<PromotionDraft>(() => emptyDraft(currency));
  function set<K extends keyof PromotionDraft>(key: K, value: PromotionDraft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }
  const [startCalOpen, setStartCalOpen] = useState(false);
  const [endCalOpen, setEndCalOpen] = useState(false);
  const startCalRef = useRef<HTMLButtonElement>(null);
  const endCalRef = useRef<HTMLButtonElement>(null);
  // True once the form holds its baseline values (immediately for a new promo,
  // after hydration for an existing one) - gates the pristine-snapshot capture.
  const [hydrated, setHydrated] = useState(isNew);
  // Simple vs Advanced. Simple shows Name + Description + Discount + Applies-to
  // and leaves every other field at its default; Advanced reveals the rest.
  // Toggling only shows/hides fields - it never changes state, so switching (or
  // saving in Simple) preserves any advanced values already set. Auto-opened to
  // Advanced when editing a promo that already uses advanced options (once).
  const [mode, setMode] = useState<EditMode>('simple');

  // Show the issue-coupon tools off the LIVE form selection (not the saved
  // state) so they appear the moment you pick CODE_REQUIRED + PRIVATE - no save
  // needed. Still gated on !isNew: minting a personal coupon POSTs against a
  // real promotion id, which a never-saved "new" promo doesn't have yet.
  // Trade-off the owner accepts: coupons can be issued before the promotion's
  // private setting is saved (or if the edit is later discarded).
  const showCouponTools =
    !isNew && draft.trigger === 'CODE_REQUIRED' && draft.couponKind === 'PRIVATE';

  // The whole "How it's unlocked" section is about discount codes. Hidden with
  // the feature flag off, EXCEPT on a promotion already set to CODE_REQUIRED —
  // that one keeps the section so it stays editable (and can be switched back to
  // AUTOMATIC) rather than being stranded. `showCouponTools` already requires
  // CODE_REQUIRED, so the coupon tools follow this automatically.
  const showUnlockSection = featureFlags.promoCodes || draft.trigger === 'CODE_REQUIRED';
  const {
    data: coupons,
    isLoading: couponsLoading,
    isError: couponsError,
  } = usePromotionCoupons(showCouponTools ? routeId : null);

  // Hydrate the form when editing an existing promotion.
  useEffect(() => {
    if (!existing) return;
    setDraft(hydrateDraft(existing, currency, tz));
    setHydrated(true);
    // Land on the Basic tab so the core fields (name, discount, applies-to) show
    // first - the tabs are now separated, so auto-opening Advanced would hide
    // them. The simple-mode `advancedValuesHint` still nudges the owner to the
    // Advanced tab when this promo already has advanced options set.
  }, [existing, currency, tz]);

  // Dirty tracking: snapshot every editable field, compare against the pristine
  // snapshot captured once the form holds its baseline values. Drives the Save
  // button (lit only when dirty) and the discard-changes guard.
  const snapshot = JSON.stringify(draft);
  const pristineRef = useRef<string | null>(null);
  useEffect(() => {
    if (hydrated && pristineRef.current === null) pristineRef.current = snapshot;
  }, [hydrated, snapshot]);
  const dirty = pristineRef.current !== null && snapshot !== pristineRef.current;

  const toggleOrderType = (ot: OrderType) =>
    setDraft((d) => ({
      ...d,
      orderTypes: d.orderTypes.includes(ot)
        ? d.orderTypes.filter((x) => x !== ot)
        : [...d.orderTypes, ot],
    }));
  const togglePaymentMethodKind = (kind: PromotionPaymentMethodKind) =>
    setDraft((d) => ({
      ...d,
      paymentMethodKinds: d.paymentMethodKinds.includes(kind)
        ? d.paymentMethodKinds.filter((x) => x !== kind)
        : [...d.paymentMethodKinds, kind],
    }));
  const toggleTargetCategory = (category: TargetCategory) =>
    setDraft((d) => ({ ...d, targetSelection: toggleCategory(d.targetSelection, category) }));
  const toggleTargetProduct = (category: TargetCategory, productId: string) =>
    setDraft((d) => ({
      ...d,
      targetSelection: toggleProduct(d.targetSelection, category, productId),
    }));

  const pending = create.isPending || update.isPending;

  // Validation + payload assembly live in the pure ../domain module (testable
  // without React). Here we only assemble the draft from state and map a failing
  // field back to its tab + toast — a required/invalid field may live on the tab
  // that isn't showing, so switch to it before toasting.
  const buildErrorUi: Record<
    PromotionBuildError,
    { mode: EditMode; message: string; timeout: number }
  > = {
    name: { mode: 'simple', message: tp.giveNameError, timeout: 2500 },
    orderType: { mode: 'advanced', message: tp.pickOrderTypeError, timeout: 2500 },
    target: { mode: 'simple', message: tp.chooseDiscountError, timeout: 2500 },
    dateRange: { mode: 'simple', message: tp.endBeforeStartError, timeout: 3000 },
    schedule: { mode: 'advanced', message: tp.scheduleWindowError, timeout: 3000 },
  };

  function buildBody(): CreatePromotionBody | null {
    const result = buildPromotionBody(draft, tz, categories);
    if (result.ok) return result.body;
    const ui = buildErrorUi[result.field];
    setMode(ui.mode);
    f7.toast.show({ text: ui.message, closeTimeout: ui.timeout, position: 'bottom' });
    return null;
  }

  function handleSave() {
    const body = buildBody();
    if (!body) return;
    const done = () => {
      // Saved - clear the dirty flag before navigating so the guard doesn't
      // prompt "discard changes?" on the way back.
      setUnsavedDirty(false);
      f7.toast.show({ text: tp.saved, closeTimeout: 2000, position: 'bottom' });
      // The route's own router: `f7.views.current` is undefined in this nested
      // view (Settings popup on desktop / Settings tab on mobile).
      f7router.back();
    };
    if (isNew) create.mutate(body, { onSuccess: done });
    else update.mutate({ promotionId: routeId, data: body }, { onSuccess: done });
  }

  // Flatten + de-dupe (by id) across all menus so a category/product shared by
  // several menus shows once.
  const categories = Array.from(
    new Map(
      menuTrees
        .flatMap((t) => t.categories)
        .map((c) => [
          c.id,
          {
            id: c.id,
            name: loc(c.name),
            products: c.products.map((p) => ({ id: p.id, name: loc(p.name) })),
            productIds: c.products.map((p) => p.id),
          },
        ]),
    ).values(),
  );

  // Live check on the current form: are any Advanced-only values set? Drives the
  // Simple-mode hint so hidden config (e.g. a schedule from before) isn't silent.
  const advancedValuesSet = usesAdvancedFeatures({
    trigger: draft.trigger,
    weeklySchedule: draft.weeklySchedule.length > 0 ? draft.weeklySchedule : null,
    startsAt: draft.startDate ? (existing?.startsAt ?? 'set') : null,
    endsAt: draft.endDate ? (existing?.endsAt ?? 'set') : null,
    minSubtotalCents: draft.minSubtotal.trim() ? 1 : 0,
    stackable: draft.stackable,
    priority: parseInt(draft.priority || '0', 10),
    maxDiscountCents: draft.maxDiscount.trim() ? 1 : null,
    customerVisible: draft.customerVisible,
    orderTypes: draft.orderTypes,
    paymentMethodKinds: draft.paymentMethodKinds,
  });

  return (
    <Page name="promotion-edit">
      <Navbar title={isNew ? tp.newPromotion : tp.editPromotion} backLink={t.back}>
        <SettingsSaveNav isDirty={dirty} saving={pending} onSave={handleSave} />
      </Navbar>

      <Block>
        <Segmented strong>
          <Button active={mode === 'simple'} onClick={() => setMode('simple')}>
            {tp.simpleMode}
          </Button>
          <Button active={mode === 'advanced'} onClick={() => setMode('advanced')}>
            {tp.advancedMode}
          </Button>
        </Segmented>
        {mode === 'simple' && advancedValuesSet && (
          <p
            className="margin-top-half"
            style={{ fontSize: 13, color: 'var(--f7-list-item-subtitle-text-color)' }}
          >
            {tp.advancedValuesHint}
          </p>
        )}
      </Block>

      {/* BASIC tab - the core fields every promotion needs (required on create).
          Rendered only in simple mode so the tabs don't duplicate fields; state
          persists across tab switches, so switching never loses what was typed. */}
      {mode === 'simple' && (
        <>
          <BlockTitle>{tp.details}</BlockTitle>
          <List strongIos insetIos dividersIos>
            <ListInput
              label={tp.name}
              type="text"
              placeholder={tp.namePlaceholder}
              value={draft.nameEn}
              onInput={(e) => set('nameEn', e.target.value)}
            />
            <ListInput
              label={tp.description}
              type="textarea"
              placeholder={tp.descriptionPlaceholder}
              value={draft.descEn}
              onInput={(e) => set('descEn', e.target.value)}
            />
          </List>

          <BlockTitle>{tp.discount}</BlockTitle>
          <List strongIos insetIos dividersIos>
            {SelectRow({
              label: tp.discountTypeLabel,
              value: draft.discountType,
              options: [
                { value: 'PERCENTAGE', label: tp.discountType.percentage },
                { value: 'FIXED_AMOUNT', label: tp.discountType.fixedAmount },
                { value: 'FREE_DELIVERY', label: tp.discountType.freeDelivery },
              ],
              onChange: (v) => set('discountType', v),
              cancelText: t.cancel,
            })}
            {draft.discountType === 'PERCENTAGE' && (
              <ListInput
                label={tp.percentOff}
                type="number"
                min="0"
                max="100"
                value={draft.percentValue}
                onInput={(e) => set('percentValue', e.target.value)}
              >
                <span slot="info">{tp.percentOffHint}</span>
              </ListInput>
            )}
            {draft.discountType === 'FIXED_AMOUNT' && (
              <ListInput
                label={tp.amountOff(currency)}
                type="text"
                placeholder={tp.amountOffPlaceholder}
                value={draft.amountValue}
                onInput={(e) => set('amountValue', e.target.value)}
              />
            )}
          </List>

          <BlockTitle>{tp.appliesto}</BlockTitle>
          <List strongIos insetIos dividersIos>
            <ListItem
              radio
              name="promotion-target-type"
              value="ENTIRE_ORDER"
              title={tp.scope.wholeOrder}
              checked={draft.targetType === 'ENTIRE_ORDER'}
              onChange={() => set('targetType', 'ENTIRE_ORDER')}
              data-testid="promotion-target-whole-order"
            />
            <ListItem
              radio
              name="promotion-target-type"
              value="SELECTED_ITEMS"
              title={tp.scope.selectedItems}
              checked={draft.targetType === 'SELECTED_ITEMS'}
              onChange={() => set('targetType', 'SELECTED_ITEMS')}
              data-testid="promotion-target-selected-items"
            />
          </List>
          {draft.targetType === 'SELECTED_ITEMS' &&
            (itemsLoading ? (
              <List strongIos insetIos dividersIos>
                <ListItem title={tp.loadingMenu} data-testid="promotion-targets-loading" />
              </List>
            ) : itemsError ? (
              <List strongIos insetIos dividersIos>
                <ListItem title={tp.menuLoadError} data-testid="promotion-targets-error" />
              </List>
            ) : categories.length === 0 ? (
              <List strongIos insetIos dividersIos>
                <ListItem title={tp.noCategoriesYet} data-testid="promotion-targets-empty" />
              </List>
            ) : (
              <List strongIos insetIos dividersIos accordionList data-testid="promotion-targets">
                {categories.map((c) => {
                  const state = categoryState(draft.targetSelection, c);
                  return (
                    <ListItem
                      key={c.id}
                      accordionItem
                      data-testid={`promotion-target-category-${c.id}`}
                    >
                      <div slot="media" onClick={(e: MouseEvent) => e.stopPropagation()}>
                        <TriStateCheckbox
                          state={state}
                          ariaLabel={c.name}
                          onClick={() => toggleTargetCategory(c)}
                          testId={`promotion-target-category-cb-${c.id}`}
                        />
                      </div>
                      <span slot="title">{c.name}</span>
                      <span slot="after" data-testid={`promotion-target-category-count-${c.id}`}>
                        {`${checkedProductCount(draft.targetSelection, c)} / ${c.productIds.length}`}
                      </span>
                      <AccordionContent>
                        {c.products.length === 0 ? (
                          <Block className="text-color-gray" style={{ padding: '8px 16px' }}>
                            {tp.noProductsYet}
                          </Block>
                        ) : (
                          <List strongIos insetIos dividersIos>
                            {c.products.map((p) => (
                              <ListItem key={p.id} data-testid={`promotion-target-product-${p.id}`}>
                                <div slot="media" onClick={(e: MouseEvent) => e.stopPropagation()}>
                                  <TriStateCheckbox
                                    state={
                                      isProductChecked(draft.targetSelection, c, p.id)
                                        ? 'checked'
                                        : 'empty'
                                    }
                                    size={20}
                                    ariaLabel={p.name}
                                    onClick={() => toggleTargetProduct(c, p.id)}
                                    testId={`promotion-target-product-cb-${p.id}`}
                                  />
                                </div>
                                <span slot="title">{p.name}</span>
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </AccordionContent>
                    </ListItem>
                  );
                })}
              </List>
            ))}

          <BlockTitle>{tp.dateWindow}</BlockTitle>
          <List strongIos insetIos dividersIos>
            <ListItem title={tp.starts}>
              <div slot="after" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  ref={startCalRef}
                  type="button"
                  className="no-ripple"
                  aria-label={tp.startDate}
                  onClick={() => setStartCalOpen(true)}
                  style={DATE_PILL_STYLE}
                >
                  {formatDateChip(draft.startDate) || '-'}
                </button>
                {draft.startDate && (
                  <ChipInput
                    type="time"
                    value={draft.startTime}
                    onChange={(v) => set('startTime', v)}
                    aria-label={tp.starts}
                  />
                )}
              </div>
            </ListItem>
            <ListItem title={tp.ends}>
              <div slot="after" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  ref={endCalRef}
                  type="button"
                  className="no-ripple"
                  aria-label={tp.endDate}
                  onClick={() => setEndCalOpen(true)}
                  style={DATE_PILL_STYLE}
                >
                  {formatDateChip(draft.endDate) || '-'}
                </button>
                {draft.endDate && (
                  <ChipInput
                    type="time"
                    value={draft.endTime}
                    onChange={(v) => set('endTime', v)}
                    aria-label={tp.ends}
                  />
                )}
              </div>
            </ListItem>
          </List>

          <MonthCalendarPopover
            open={startCalOpen}
            onClose={() => setStartCalOpen(false)}
            anchorEl={startCalRef.current}
            selectedDate={draft.startDate}
            onSelect={(d) => {
              set('startDate', d);
              setStartCalOpen(false);
            }}
          />
          <MonthCalendarPopover
            open={endCalOpen}
            onClose={() => setEndCalOpen(false)}
            anchorEl={endCalRef.current}
            selectedDate={draft.endDate}
            onSelect={(d) => {
              set('endDate', d);
              setEndCalOpen(false);
            }}
          />
        </>
      )}

      {/* ADVANCED tab - every optional knob. No basic fields repeated here; all
          have safe defaults, so a promotion can be created from the basic tab
          alone. State persists when switching back to basic. */}
      {mode === 'advanced' && (
        <>
          {/* Percentage cap - only meaningful for a % discount (whose type lives
              on the basic tab; state is shared, so this reacts to it live). */}
          {draft.discountType === 'PERCENTAGE' && (
            <>
              <BlockTitle>{tp.discount}</BlockTitle>
              <List strongIos insetIos dividersIos>
                <ListInput
                  label={tp.maxDiscount(currency)}
                  type="text"
                  placeholder={tp.maxDiscountPlaceholder}
                  value={draft.maxDiscount}
                  onInput={(e) => set('maxDiscount', e.target.value)}
                />
              </List>
            </>
          )}

          <BlockTitle>{tp.conditions}</BlockTitle>
          <List strongIos insetIos dividersIos>
            {ALL_ORDER_TYPES.map((ot) => (
              <ListItem key={ot} title={orderTypeLabel[ot]}>
                <ToggleSwitch
                  slot="after"
                  checked={draft.orderTypes.includes(ot)}
                  onChange={() => toggleOrderType(ot)}
                />
              </ListItem>
            ))}
            <ListInput
              label={tp.minimumOrder(currency)}
              type="text"
              placeholder={tp.minimumOrderPlaceholder}
              value={draft.minSubtotal}
              onInput={(e) => set('minSubtotal', e.target.value)}
            />
          </List>

          <BlockTitle>{tp.paymentMethodsTitle}</BlockTitle>
          <List strongIos insetIos dividersIos>
            {ALL_PAYMENT_METHOD_KINDS.map((kind) => (
              <ListItem key={kind} title={paymentMethodLabel[kind]}>
                <ToggleSwitch
                  slot="after"
                  checked={draft.paymentMethodKinds.includes(kind)}
                  onChange={() => togglePaymentMethodKind(kind)}
                />
              </ListItem>
            ))}
          </List>
          <Block>
            <p
              className="margin-top-half"
              style={{ fontSize: 13, color: 'var(--f7-list-item-subtitle-text-color)' }}
            >
              {tp.paymentMethodsFooter}
            </p>
          </Block>

          {/* Day-of-week + time windows (the restaurant take on salon's off-peak
          hours). Keyed on the promotion id so the editor re-seeds once an
          existing promotion's schedule has loaded. */}
          <WeeklyScheduleSection
            key={existing?.id ?? 'new'}
            initial={existing?.weeklySchedule ?? []}
            onChange={(w) => set('weeklySchedule', w)}
          />

          {/* Everything under "How it's unlocked" is about codes, so with codes
          flagged off the heading and its card go too — leaving them would render
          an empty card under a title, and a one-option picker is not a choice.
          An existing CODE_REQUIRED promotion keeps the whole section: authored
          while the flag was on, it must stay readable and switchable back to
          AUTOMATIC instead of being stranded in a state the editor can't show. */}
          {showUnlockSection && (
            <>
              <BlockTitle>{tp.howUnlocked}</BlockTitle>
              <List strongIos insetIos dividersIos>
                {SelectRow({
                  label: tp.triggerLabel,
                  value: draft.trigger,
                  options: [
                    { value: 'AUTOMATIC', label: tp.trigger.automatic },
                    { value: 'CODE_REQUIRED', label: tp.trigger.codeRequired },
                  ],
                  onChange: (v) => set('trigger', v),
                  cancelText: t.cancel,
                })}
                {draft.trigger === 'CODE_REQUIRED' &&
                  SelectRow({
                    label: tp.codeTypeLabel,
                    value: draft.couponKind,
                    options: [
                      { value: 'PUBLIC', label: tp.codeType.shared },
                      { value: 'PRIVATE', label: tp.codeType.personal },
                    ],
                    onChange: (v) => set('couponKind', v),
                    cancelText: t.cancel,
                  })}
                {/* Array (not a Fragment) so F7's <List> flattens these into its <ul> -
            a Fragment renders outside the ul, leaking the default <li> bullet. */}
                {draft.trigger === 'CODE_REQUIRED' &&
                  draft.couponKind === 'PUBLIC' && [
                    <ListInput
                      key="code"
                      label={tp.code}
                      type="text"
                      placeholder={tp.codePlaceholder}
                      value={draft.code}
                      onInput={(e) => set('code', e.target.value.toUpperCase())}
                    />,
                    <ListInput
                      key="maxRedemptions"
                      label={tp.maxRedemptions}
                      type="number"
                      min="1"
                      placeholder={tp.maxRedemptionsPlaceholder}
                      value={draft.maxRedemptions}
                      onInput={(e) => set('maxRedemptions', e.target.value)}
                    />,
                    <ListItem key="oncePerCustomer" title={tp.oncePerCustomer}>
                      <ToggleSwitch
                        slot="after"
                        checked={draft.oncePerCustomer}
                        onChange={(v) => setDraft((d) => ({ ...d, oncePerCustomer: v }))}
                      />
                    </ListItem>,
                  ]}
              </List>
            </>
          )}

          {/* Personal coupons are minted against a PRIVATE promotion - issue one,
              then see the running history of who holds each and whether used.
              Shown off the live selection so it appears as soon as PRIVATE is
              chosen, and placed right under the code config it belongs with. */}
          {showCouponTools && (
            <div className="margin-bottom">
              <IssueCouponSection promotionId={routeId} />
              <CouponHistoryList
                title={tp.issuedCoupons}
                emptyText={tp.noCouponsIssued}
                items={coupons ?? []}
                primary="customer"
                isLoading={couponsLoading}
                isError={couponsError}
              />
            </div>
          )}

          <BlockTitle>{tp.visibilityStacking}</BlockTitle>
          <List strongIos insetIos dividersIos>
            <ListItem title={tp.showOnStorefront} footer={tp.showOnStorefrontFooter}>
              <ToggleSwitch
                slot="after"
                checked={draft.customerVisible}
                onChange={(v) => setDraft((d) => ({ ...d, customerVisible: v }))}
              />
            </ListItem>
            <ListItem title={tp.stackable} footer={tp.stackableFooter}>
              <ToggleSwitch
                slot="after"
                checked={draft.stackable}
                onChange={(v) => setDraft((d) => ({ ...d, stackable: v }))}
              />
            </ListItem>
            <ListInput
              label={tp.priority}
              type="number"
              value={draft.priority}
              onInput={(e) => set('priority', e.target.value)}
            >
              <span slot="info">{tp.priorityHint}</span>
            </ListInput>
          </List>
        </>
      )}
    </Page>
  );
}

export default PromotionEditPage;
